import { useState, useEffect, useRef } from 'react';
import useReveal from '../../hooks/useReveal';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import BgImage from '../../assets/images/BackgroundImage.png';
import star from '../../assets/images/star.png';
import infrastructure from '../../assets/images/infrastructure.png';
import { IoIosSearch, IoIosSend } from 'react-icons/io';
import { IoMdArrowDropdown } from 'react-icons/io';
import blog1 from '../../assets/images/blog1.png';
import blog2 from '../../assets/images/blog2.png';
import blog3 from '../../assets/images/blog3.png';
import blog4 from '../../assets/images/blog4.png';
import blog5 from '../../assets/images/blog5.png';
import SEO from '../../components/SEO';
import Footer from '../../components/footer';
import {
  getBlogs,
  getCategories,
  subscribeToBlog,
  BLOG_API_BASE_URL,
} from '../../hooks/remote/blogApi';
import { toast } from 'react-toastify';
import { extractErrorMessage } from '../../hooks/constants';

const BlogPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [searchQuery, setSearchQuery] = useState('');
  const [subscriberEmail, setSubscriberEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState(null);
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [featuredRef, featuredVisible] = useReveal();
  const [gridRef, gridVisible] = useReveal();
  const [subscribeRef, subscribeVisible] = useReveal();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const stripToPreview = (html, maxLength) => {
    if (!html) return '';

    // Create a temporary div to parse HTML and extract plain text
    const temp = document.createElement('div');
    temp.innerHTML = html;

    // Get plain text only (no HTML tags)
    let text = temp.textContent || temp.innerText || '';

    // Clean up extra whitespace
    text = text.replace(/\s+/g, ' ').trim();

    // Truncate to desired length
    if (text.length > maxLength) {
      text = text.substring(0, maxLength).trim() + '...';
    }

    return text;
  };

  const activeCategory = searchParams.get('category') || 'all';

  useEffect(() => {
    fetchData();

    // Live refresh when another tab updates blogs (e.g., from admin)
    const onStorage = (e) => {
      if (e.key === 'blogChanged') {
        fetchData();
      }
    };
    window.addEventListener('storage', onStorage);

    // Live refresh within the same tab via a custom event
    const onCustomEvent = () => fetchData();
    window.addEventListener('blogChanged', onCustomEvent);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('blogChanged', onCustomEvent);
    };
  }, [activeCategory]);

  // Debounced search on typing (case-insensitive)
  useEffect(() => {
    const handler = setTimeout(() => {
      const trimmed = (searchQuery || '').trim();
      if (trimmed === '') {
        fetchData('');
      } else {
        // Clear category filter so search spans all categories
        setSearchParams({});
        fetchData(trimmed);
      }
    }, 500); // Reduced from 2000ms
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const fetchData = async (overrideQuery) => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (activeCategory !== 'all') {
        params.category = activeCategory;
      }
      const q = typeof overrideQuery === 'string' ? overrideQuery : searchQuery;
      const searchTerms = q ? q.toLowerCase() : '';

      // If the search term is short, the backend might return exact prefix matches only.
      // We'll fetch more and filter locally if needed for better UX.
      if (searchTerms) {
        params.search = searchTerms;
      }

      const [blogsData, categoriesData] = await Promise.all([
        getBlogs(params),
        getCategories(),
      ]);

      let filteredBlogs = Array.isArray(blogsData) ? blogsData : [];

      // If we have a search query, perform an additional client-side substring match
      // to ensure results are returned even if the backend is prefix-only.
      if (searchTerms) {
        const substringMatches = filteredBlogs.filter(
          (blog) =>
            blog.title?.toLowerCase().includes(searchTerms) ||
            blog.excerpt?.toLowerCase().includes(searchTerms),
        );

        // If local filtering produced results, use them.
        // Note: We're augmenting the backend search, not replacing it entirely.
        if (substringMatches.length > 0) {
          filteredBlogs = substringMatches;
        }
      }

      setBlogs(filteredBlogs);
      setCategories([
        { id: 'all', name: 'All Posts', slug: 'all' },
        ...categoriesData,
      ]);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      const errorMsg = extractErrorMessage(err);
      setError(errorMsg || 'Failed to load posts');
      toast.error(errorMsg || 'Failed to load posts', { theme: 'colored' });
      setLoading(false);
    }
  };

  const handleCategoryChange = (categorySlug) => {
    if (categorySlug === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category: categorySlug });
    }
    setIsOpen(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmed = (searchQuery || '').trim();
    if (!trimmed) {
      setSearchQuery('');
      fetchData('');
      return;
    }
    setSearchQuery(trimmed);
    // Clear category filter so search spans all categories
    setSearchParams({});
    fetchData(trimmed);
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    const email = subscriberEmail.trim();
    if (!email) {
      setSubscribeStatus({
        type: 'error',
        message: 'Please enter your email.',
      });
      return;
    }
    setSubscribeLoading(true);
    setSubscribeStatus(null);
    try {
      const result = await subscribeToBlog(email);
      if (result.success) {
        setSubscribeStatus({
          type: 'success',
          message: result.message || 'Subscription successful!',
        });
        setSubscriberEmail('');
        toast.success(result.message || 'Subscription successful!', {
          theme: 'colored',
        });
      } else {
        const errorMsg = result.message || 'Failed to subscribe';
        setSubscribeStatus({
          type: 'error',
          message: errorMsg,
        });
        toast.error(errorMsg, { theme: 'colored' });
      }
    } catch (error) {
      const errorMsg = extractErrorMessage(error);
      setSubscribeStatus({ type: 'error', message: errorMsg });
      toast.error(errorMsg, { theme: 'colored' });
    } finally {
      setSubscribeLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${BLOG_API_BASE_URL.replace('/api', '')}${cleanPath}`;
  };

  if (loading && blogs.length === 0) {
    return (
      <div className="bg-blue-50 dark:bg-[#141D26] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300 text-lg">
            Loading blogs...
          </p>
        </div>
      </div>
    );
  }

  const featuredBlog = blogs.length > 0 ? blogs[0] : null;
  const gridBlogs = blogs.length > 1 ? blogs.slice(1) : [];

  const activeCategoryName =
    categories.find((cat) => cat.slug === activeCategory)?.name || 'All Posts';

  if (error && blogs.length === 0) {
    return (
      <div className="bg-blue-50 dark:bg-[#141D26] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 dark:text-red-400 text-lg mb-4">{error}</p>
          <button
            onClick={() => fetchData()}
            className="bg-blue-500 hover:bg-blue-600 dark:bg-primaryBlue dark:hover:bg-primaryBlue/90 text-white px-6 py-2 rounded"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SEO
        title="Blog | News and Insights | CloudVantage"
        description="Learn about cloud services, cloud infrastructure solutions, and discover our latest product updates, partnership announcements, user stories, and more."
        keywords="cloud blog, cloud infrastructure, cloud services, cloud computing, cloud security, cloud storage, cloudvantage news"
        author="CloudVantage"
        ogTitle="Blog | News and Insights | CloudVantage"
        ogDescription="Learn about cloud services, cloud infrastructure solutions, and discover our latest product updates."
        ogImage="/assets/images/cloudvantage-blog-og.jpg"
        ogUrl={typeof window !== 'undefined' ? window.location.href : ''}
      />

      <div
        className="bg-cover bg-center h-full items-center justify-center bg-blue-50 dark:bg-[#141D26] min-h-screen"
        //style={{ backgroundImage: `url(${BgImage})` }}
      >
        <div className="p-6 gap-2 flex flex-col items-center justify-center animate-reveal">
          <h1 className="text-4xl font-bold text-black dark:text-white leading-tight crimson-font ">
            News and Insights
          </h1>
          <p className="text-slate-600 dark:text-gray-300 text-center mb-5 md:w-1/2">
            Learn about cloud services, cloud infrastructure solutions, and
            discover our latest product updates, partnership announcements, user
            stories, and more.
          </p>
        </div>

        {/* <form
          onSubmit={handleSubscribe}
          className="flex items-center mt-6 bg-white dark:bg-slate-800 shadow-md rounded overflow-hidden w-5/6 md:w-1/2 border border-slate-200 dark:border-[#c4c4c432] justify-between p-2 gap-x-4 mx-auto"
        >
          <div className="flex w-full items-center gap-3 px-2">
            <div className="md:flex items-center hidden">
              <img src={star} alt="icon" className="w-5 h-5 opacity-70" />
            </div>
            <input
              type="email"
              placeholder="Enter your email address"
              className="flex-grow p-2 md:p-3 text-slate-700 dark:text-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-gray-400 focus-within:outline-none border-none outline-none"
              value={subscriberEmail}
              onChange={(e) => setSubscriberEmail(e.target.value)}
              disabled={subscribeLoading}
              required
            />
          </div>
          <div className="flex items-center gap-4 pr-2">
            <button
              type="submit"
              className="bg-blue-500 dark:bg-primaryBlue text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-blue-600 dark:hover:bg-primaryBlue/90 transition"
              disabled={subscribeLoading}
              aria-label="Subscribe"
            >
              {subscribeLoading ? (
                <span className="text-xs font-semibold">...</span>
              ) : (
                <IoIosSend />
              )}
            </button>
          </div>
        </form>
        {subscribeStatus && (
          <p
            className={`mt-2 text-center text-sm mx-auto w-5/6 md:w-1/2 ${
              subscribeStatus.type === 'success'
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400'
            }`}
          >
            {subscribeStatus.message}
          </p>
        )} */}

        <div className="grid md:flex items-center gap-3 pb-6 w-full md:w-2/3 lg:w-1/2 px-4 md:px-0 mx-auto">
          {/* Search */}
          <form
            onSubmit={handleSearch}
            className="flex items-center text-slate-600 dark:text-white dark:placeholder-gray-400 w-full outline-none border border-slate-200 dark:border-[#c4c4c432] rounded bg-transparent bg-white dark:bg-slate-700"
          >
            <input
              type="text"
              placeholder="Search posts..."
              className="w-full py-3 bg-transparent border-none outline-none focus:outline-none focus:ring-0"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              type="button"
              className="px-5"
              onClick={() => {
                const trimmed = (searchQuery || '').trim();
                if (!trimmed) {
                  setSearchQuery('');
                  fetchData('');
                } else {
                  setSearchQuery(trimmed);
                  setSearchParams({});
                  fetchData(trimmed);
                }
              }}
            >
              <IoIosSearch className="text-slate-500 dark:text-gray-400" />
            </button>
          </form>

          {/* Category Filter Dropdown */}
          <div ref={dropdownRef} className="relative shrink-0">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center gap-1 border border-slate-200 dark:border-[#c4c4c432] rounded py-[14px] px-5 bg-white dark:bg-slate-700 text-black dark:text-white font-semibold text-sm whitespace-nowrap"
            >
              {activeCategoryName}
              <IoMdArrowDropdown
                className={`text-black dark:text-white transition-transform ${
                  isOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {isOpen && (
              <div className="absolute left-0 mt-1 z-10 bg-white dark:bg-slate-800 border border-slate-200 dark:border-[#c4c4c432] rounded-lg shadow-md min-w-[160px]">
                {categories.map((category) => (
                  <button
                    key={category.id || category.slug}
                    onClick={() => handleCategoryChange(category.slug)}
                    className={`block w-full text-left px-4 py-2 text-sm font-medium transition
                      ${
                        activeCategory === category.slug
                          ? 'bg-blue-500 dark:bg-primaryBlue text-white'
                          : 'text-slate-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-slate-700'
                      }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Featured Section — only shown on "All Posts" */}
        {activeCategory === 'all' && (
          featuredBlog ? (
            <div ref={featuredRef} className={`grid grid-cols-1 md:grid-cols-3 items-center px-4 md:px-0 overflow-hidden ${featuredVisible ? 'animate-reveal' : 'opacity-0'}`}>
              <img
                src={getImageUrl(featuredBlog.featuredImage) || infrastructure}
                alt={featuredBlog.title}
                className="aspect-video object-cover md:col-span-2 rounded-xl"
                onError={(e) => {
                  e.currentTarget.src = infrastructure;
                }}
              />
              <div className="md:px-6 pt-6 md:pt-0">
                <div className="flex-1 flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {featuredBlog.Author?.name || 'Author'}
                    </p>
                    <span>•</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCategoryChange(featuredBlog.Category?.slug || 'all');
                      }}
                      className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 p-2 rounded-md text-xs font-semibold hover:bg-blue-200 dark:hover:bg-blue-900/50"
                    >
                      {featuredBlog.Category?.name || 'Uncategorized'}
                    </button>
                  </div>
                  <h2 className="font-bold text-2xl text-gray-900 dark:text-white leading-tight crimson-font">
                    {featuredBlog.title}
                  </h2>
                  <div
                    className="blog-preview text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-2"
                    dangerouslySetInnerHTML={{
                      __html:
                        featuredBlog.excerpt ||
                        featuredBlog.content?.substring(0, 300) + '...',
                    }}
                  />
                </div>
                <div className="mt-6">
                  <button
                    onClick={() => navigate(`/blog/${featuredBlog.slug}`)}
                    className="bg-blue-500 hover:bg-blue-600 dark:bg-primaryBlue dark:hover:bg-primaryBlue/90 text-white font-semibold text-sm px-5 py-4 rounded cursor-pointer transition-colors"
                  >
                    Read more
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-6/7 h-96 mx-auto grid grid-cols-1 sm:grid-cols-2 rounded-xl overflow-hidden shadow-lg">
              <img
                src={infrastructure}
                alt="Cloud Infrastructure"
                className="w-full h-full object-cover"
              />
              <div className="bg-white dark:bg-slate-800 flex flex-col justify-between p-8">
                <div>
                  <h2 className="font-bold text-3xl text-gray-900 dark:text-white mb-4 leading-tight crimson-font">
                    No featured blog yet
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-sm sm:text-base">
                    Check back soon for our latest articles.
                  </p>
                </div>
              </div>
            </div>
          )
        )}

        {/* Blog Grid */}
        <div ref={gridRef} className={`grid grid-cols-1 ${
            (activeCategory === 'all' ? gridBlogs : blogs).length < 4
              ? 'md:grid-cols-2 lg:grid-cols-3'
              : 'md:grid-cols-3 lg:grid-cols-4'
          } gap-6 px-4 md:px-0 pt-6 ${gridVisible ? 'animate-reveal' : 'opacity-0'}`}>
          {(() => {
            const displayBlogs = activeCategory === 'all' ? gridBlogs : blogs;
            return displayBlogs.length === 0 && !loading ? (
              <p className="col-span-full text-center text-gray-500 dark:text-gray-400">
                No posts yet.
              </p>
            ) : (
              displayBlogs.map((b, index) => {
              const imageUrl = getImageUrl(b.featuredImage);
              const fallbackImages = [blog1, blog2, blog3, blog4, blog5];
              return (
                <div
                  key={b.id || index}
                  className="overflow-hidden cursor-pointer"
                  onClick={() => navigate(`/blog/${b.slug}`)}
                >
                  <img
                    src={imageUrl || fallbackImages[index % 5]}
                    className="aspect-video rounded-lg object-cover"
                    alt={b.title}
                    onError={(e) => {
                      if (!imageUrl) {
                        e.currentTarget.src = fallbackImages[index % 5];
                      } else {
                        e.currentTarget.style.display = 'none';
                      }
                    }}
                  />
                  <div className="pt-4">
                    <div className="flex items-center gap-2 mb-2 text-sm">
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {b.Author?.name || 'Author'}
                      </p>
                      <span>•</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCategoryChange(b.Category?.slug || 'all');
                        }}
                        className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 p-2 rounded-md text-xs font-semibold hover:bg-blue-200 dark:hover:bg-blue-900/50"
                      >
                        {b.Category?.name || 'Uncategorized'}
                      </button>
                      
                      {/* <span className="font-semibold text-gray-600 dark:text-gray-300">
                        • {b.readTime || '5 min read'}
                      </span> */}
                    </div>

                    <h3 className="font-bold text-lg mb-1 text-black dark:text-white crimson-font leading-tight">
                      {b.title}
                    </h3>
                    <div
                      className="blog-preview text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-2"
                      dangerouslySetInnerHTML={{
                        __html:
                          b.excerpt || b.content?.substring(0, 200) + '...',
                      }}
                    />

                    <div className="flex justify-between items-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/blog/${b.slug}`);
                        }}
                        className="text-blue-600 dark:text-blue-400 font-semibold text-sm hover:underline"
                      >
                        Read More
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          );
        })()}
        </div>

        <div ref={subscribeRef} className={`flex flex-col items-center text-center my-16 px-4 md:px-0 ${subscribeVisible ? 'animate-reveal' : 'opacity-0'}`}>
          <h3 className="text-3xl font-bold text-black dark:text-white crimson-font">
            Never miss a single news
          </h3>
          <p className="mt-4 text-gray-700 dark:text-gray-300 max-w-xl">
            Stay informed with all the latest updates and news about our events.
            Never miss a moment of our exciting and innovative gatherings in the
            world of technology and beyond.
          </p>
          <form
            onSubmit={handleSubscribe}
            className="flex gap-2 mt-6 w-full max-w-md"
          >
            <input
              type="email"
              placeholder="Enter your email address"
              className="text-slate-600 dark:text-white dark:placeholder-gray-400 flex-1 outline-none border border-slate-200 dark:border-[#c4c4c432] rounded p-3 bg-white dark:bg-slate-700"
              value={subscriberEmail}
              onChange={(e) => setSubscriberEmail(e.target.value)}
              disabled={subscribeLoading}
              required
            />
            <button
              type="submit"
              className="bg-blue-500 dark:bg-primaryBlue text-white py-3 px-5 rounded shrink-0"
              disabled={subscribeLoading}
              aria-label="Subscribe"
            >
              {subscribeLoading ? (
                <span className="text-xs font-semibold">...</span>
              ) : (
                <span>Subscribe!</span>
              )}
            </button>
          </form>
          {subscribeStatus && (
            <p
              className={`mt-2 text-sm ${
                subscribeStatus.type === 'success'
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              }`}
            >
              {subscribeStatus.message}
            </p>
          )}
        </div>
      </div>
      {/* <Footer /> */}
    </div>
  );
};

export default BlogPage;